import { Maybe } from 'graphql/jsutils/Maybe'
import { NextPage } from 'next'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { ProjectMetaProvider } from '@/contexts/ProjectMetaContext'
import { Seo, VideoJsonLd } from '@/layout/Seo'
import { useDataForVideo } from '@/layout/Seo/SeoHelper'
import { GetAppBannerProvider } from '@/organisms/GetAppBanner'
import { getPageByPath } from '@/services/CmsService/Pages'
import { createVideoDeepLink } from '@/services/DeepLinkService'
import { ShopifyStoreConfig } from '@/services/EarlyAccessService'
import { getGuildStatus } from '@/services/GuildUserService/GuildUserService'
import { getContentfulProjectTheme } from '@/services/ProjectThemeService'
import {
  getContentVideo,
  getProjectMeta,
  GetVideoSeoMetaResponse,
  getWatchProjectEarlyAccess,
  Project,
  ProjectMeta,
} from '@/services/ProjectsService'
import { fetchPageDataContext, PageDataContext, PageModel } from '@/services/RenderService'
import { getShopifyConfigurationForProject } from '@/services/ShopifyConfigurationService'
import { ProjectTheme } from '@/types/codegen-contentful'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { getServerSidePropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { shouldHideProjectFromSearchEngines } from '@/utils/seo/searchEngineHelper'
import { loadTranslations } from '@/utils/translate/translate-server'
import { WatchVideoView } from '@/views/WatchVideoView'

interface PageProps extends GetVideoSeoMetaResponse {
  deepLink: string | null
  locale: string
  path: string
  projectMeta: ProjectMeta
  shopifyStore?: ShopifyStoreConfig
  projectData: Project
  pageDataContext: Maybe<PageDataContext>
  page: Maybe<PageModel>
  contentfulProjectTheme: Maybe<ProjectTheme>
  isGuildMember?: boolean
  pageSlug: string
}

export const WatchVideoPage: NextPage<PageProps> = (props) => {
  const video = props.video
  const { meta, jsonLd } = useDataForVideo(
    props.locale,
    props.logoCloudinaryPath,
    props.projectName,
    props.projectSlug,
    video,
    props.pageSlug,
  )
  const shouldHide = shouldHideProjectFromSearchEngines(props.projectMeta)

  return (
    <>
      <Seo {...meta} nofollow={shouldHide} noindex={shouldHide} path={props.path} />
      <VideoJsonLd {...jsonLd} />
      <ProjectMetaProvider project={props.projectMeta}>
        <GetAppBannerProvider deepLink={props.deepLink} projectSlug={props.projectSlug} context="video">
          <WatchVideoView
            page={props.page}
            pageDataContext={props.pageDataContext}
            contentfulProjectTheme={props.contentfulProjectTheme}
            projectData={props.projectData}
            isGuildMember={props.isGuildMember}
            projectName={props.projectName}
            projectSlug={props.projectSlug}
            video={video}
            shopifyStore={props.shopifyStore}
            pageSlug={props.pageSlug}
          />
        </GetAppBannerProvider>
      </ProjectMetaProvider>
    </>
  )
}

export const getServerSideProps = getServerSidePropsErrorHandler<PageProps>(
  async ({ federationClient, locale, next404, nextRedirect, params, preview }) => {
    const guid = getItemOrFirstEntry(params.guid)
    const page = getItemOrFirstEntry(params.page)
    const projectSlug = getItemOrFirstEntry(params.project)

    if (!guid || !page || !projectSlug) return next404()
    if (projectSlug === slugs.theChosen) return nextRedirect(paths.watch.index)

    const [result, projectMeta, cmsPage] = await Promise.all([
      getContentVideo({ guid }, federationClient),
      getProjectMeta({ slug: projectSlug }, federationClient, { locale, preview: false }),
      getPageByPath(`${paths.watch.index}/${params.project}/videos/${page}/${guid}`, {
        preview,
        locale,
      }),
    ])

    const shopifyUrl = projectMeta?.projectType === 'special' ? slugs.dryBar : projectSlug

    if (!projectMeta) return nextRedirect(paths.watch.index)
    if (!result?.video || !result?.projectName) return next404()

    const path = `${paths.watch.index}/${projectSlug}/videos/${page}/${guid}`
    const name = result?.video && 'name' in result.video ? result.video.name : ''

    const [shopifyStore, deepLink, guildStatus, contentfulProjectTheme, projectData, pageDataContext, translations] =
      await Promise.all([
        getShopifyConfigurationForProject(shopifyUrl, { locale, preview }),
        createVideoDeepLink(
          {
            path,
            guid,
            projectSlug,
            projectName: result?.projectName,
            contentName: name as string,
            audioLocale: locale,
            captionLocale: locale,
          },
          {
            campaign: 'prompt-app-install',
            feature: 'app-prompt',
            channel: 'angel-web',
          },
        ),
        getGuildStatus(federationClient),
        getContentfulProjectTheme(projectSlug, preview),
        getWatchProjectEarlyAccess(projectSlug, projectMeta, federationClient),
        fetchPageDataContext(cmsPage, preview, locale, federationClient),
        loadTranslations(locale, ['common', 'home', 'watch', 'account', 'ads', 'app-promo']),
      ])

    return {
      props: {
        deepLink,
        locale,
        logoCloudinaryPath: result.video?.project?.logoCloudinaryPath ?? '',
        path,
        projectMeta,
        projectName: result.projectName,
        projectSlug,
        video: result.video,
        shopifyStore,
        page: cmsPage,
        pageDataContext,
        contentfulProjectTheme,
        projectData,
        pageSlug: page,
        isGuildMember: Boolean(guildStatus && guildStatus?.isGuildMember),
        ...translations,
      },
    }
  },
)

export default WatchVideoPage
